import { Box, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CODE_MASTER_TYPE } from '@/constants/code-master-type';
import type { TimeInformation } from '@/features/driver/types/driveDetail';
import { formatMoney } from '@/features/driver/utils/processData';
import { useCodeMaster } from '@/modules/hooks/useCodeMaster';

type PassengerInfoShowProps = {
  data: TimeInformation[];
  isShow: boolean;
};

/**
 * 乗降するユーザ情報のみ表示する。
 */
export const CurrentPassengerInfoShow: React.FC<PassengerInfoShowProps> = ({ data, isShow }) => {
  const { t } = useTranslation();

  const paymentMethodInVehicles = useCodeMaster(CODE_MASTER_TYPE.PAYMENT_METHOD_AT_RESERVATION);

  const getPaymentMethodName = (paymentMethod: string) => {
    const paymentMethodInVehicle = paymentMethodInVehicles.find((item) => item.key === paymentMethod);

    return paymentMethodInVehicle?.value;
  };

  return (
    isShow && (
      <Box p={1} sx={{ width: '32.5%', overflowY: 'auto', height: '100%' }}>
        <Box sx={{ py: 1 }}>
          {data.map((item) => (
            <Box key={item.key}>
              {item?.dropoff?.length > 0 &&
                item.dropoff.map((dropoffInfo) => (
                  <Box key={dropoffInfo.reservationId} sx={{ pb: 1 }}>
                    <Grid
                      container
                      sx={{
                        padding: '5px 0 0 4px',
                        border: '1px solid #C0C0C0',
                        borderRadius: '5px',
                        wordBreak: 'break-all',
                      }}
                    >
                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.reservationId')}
                          <Typography component="span" sx={{ fontWeight: '600' }}>
                            {dropoffInfo.reservationId}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.name')}
                          <Typography component="span">{`${dropoffInfo.userName}${t('driveDetail.mr')}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12} display="flex">
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.passenger')}
                        </Typography>
                        <Typography>
                          {dropoffInfo.passengerTypeList.map((passenger, index) => (
                            <span key={passenger.passengerTypeId}>
                              {`${passenger.passengerTypeName}${passenger.passengerCount}${t('driveDetail.people')}`}
                              {index !== dropoffInfo.passengerTypeList.length - 1 && <br />}
                            </span>
                          ))}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.pickupTime')}
                          <Typography component="span">
                            {dayjs(dropoffInfo.latestEstimatedPickupDatetime).format('HH:mm')}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.pickupWaypoint')}
                          <Typography component="span">{`${dropoffInfo.pickupWaypointName}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.dropoffTime')}
                          <Typography component="span">
                            {dayjs(dropoffInfo.latestEstimatedDropoffDatetime).format('HH:mm')}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.dropoffWaypoint')}
                          <Typography component="span">{`${dropoffInfo.dropoffWaypointName}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.price')}
                          <Typography component="span">{`${formatMoney(dropoffInfo.price)}${t('driveDetail.yen')}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.paymentMethod')}
                          <Typography component="span">
                            {getPaymentMethodName(String(dropoffInfo.paymentMethod))}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.message')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography>{dropoffInfo.message}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

              {item?.pickup?.length > 0 &&
                item.pickup.map((pickupInfo) => (
                  <Box key={pickupInfo.reservationId} sx={{ pb: 1 }}>
                    <Grid
                      container
                      sx={{
                        padding: '5px 0 0 4px',
                        border: '1px solid #C0C0C0',
                        borderRadius: '5px',
                        wordBreak: 'break-all',
                      }}
                    >
                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.reservationId')}
                          <Typography component="span" sx={{ fontWeight: '600' }}>
                            {pickupInfo.reservationId}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: '600' }}>
                          {t('driveDetail.name')}
                          <Typography component="span">{`${pickupInfo.userName}${t('driveDetail.mr')}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12} display="flex">
                        <Typography sx={{ fontWeight: '600' }}>{t('driveDetail.passenger')}</Typography>
                        <Typography>
                          {pickupInfo.passengerTypeList.map((passenger, index) => (
                            <span key={passenger.passengerTypeId}>
                              {`${passenger.passengerTypeName}${passenger.passengerCount}${t('driveDetail.people')}`}
                              {index !== pickupInfo.passengerTypeList.length - 1 && <br />}
                            </span>
                          ))}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.pickupTime')}
                          <Typography component="span">
                            {dayjs(pickupInfo.latestEstimatedPickupDatetime).format('HH:mm')}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.pickupWaypoint')}
                          <Typography component="span">{`${pickupInfo.pickupWaypointName}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.dropoffTime')}
                          <Typography component="span">
                            {dayjs(pickupInfo.latestEstimatedDropoffDatetime).format('HH:mm')}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.dropoffWaypoint')}
                          <Typography component="span">{`${pickupInfo.dropoffWaypointName}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.price')}
                          <Typography component="span">{`${formatMoney(pickupInfo.price)}${t('driveDetail.yen')}`}</Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.paymentMethod')}
                          <Typography component="span">
                            {getPaymentMethodName(String(pickupInfo.paymentMethod))}
                          </Typography>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography noWrap sx={{ fontWeight: '600' }}>
                          {t('driveDetail.message')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography>{pickupInfo.message}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
            </Box>
          ))}
        </Box>
      </Box>
    )
  );
};
